import React from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Building, Package, Target, FileText } from 'lucide-react'
import type { ClientCoreInfoForm } from '@/types/client'
import {
  PACKAGE_OPTIONS,
  TAM_SIZE_OPTIONS,
  RETAINER_CURRENCY_OPTIONS,
} from '@/types/client'

interface ClientCoreInfoStepProps {
  data: ClientCoreInfoForm
  onChange: (data: Partial<ClientCoreInfoForm>) => void
  isLoading?: boolean
}

export function ClientCoreInfoStep({ data, onChange, isLoading }: ClientCoreInfoStepProps) {
  const handleInputChange = (field: keyof ClientCoreInfoForm, value: any) => {
    onChange({ [field]: value })
  }

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Client Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={data.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter client name"
                disabled={isLoading}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={data.company || ''}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="Enter company name"
                disabled={isLoading}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="website">Website *</Label>
            <Input
              id="website"
              value={data.website || ''}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder="https://example.com"
              disabled={isLoading}
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Package Information */}
      <Card>
        <CardHeader>
          <CardTitle>Package & Contract</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="package_signed">Package Signed</Label>
              <Select
                value={data.package_signed || undefined}
                onValueChange={(value) => handleInputChange('package_signed', value || null)}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select package type" />
                </SelectTrigger>
                <SelectContent>
                  {PACKAGE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tam_target_size">Lead List Size</Label>
              <Select
                value={data.tam_target_size || undefined}
                onValueChange={(value) => handleInputChange('tam_target_size', value || null)}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select lead list size" />
                </SelectTrigger>
                <SelectContent>
                  {TAM_SIZE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="attribution_window_days">Attribution Window (days)</Label>
            <Input
              id="attribution_window_days"
              type="number"
              value={data.attribution_window_days || 90}
              onChange={(e) => handleInputChange('attribution_window_days', parseInt(e.target.value) || 90)}
              placeholder="90"
              min="1"
              max="365"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              How many days back to look when attributing meetings to this client (default: 90 days)
            </p>
          </div>

          {/* Retainer + lifecycle — wired to clients.monthly_retainer_usd etc.
              Feeds the dashboard ROI hero "You paid" figure. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
            <div className="space-y-2">
              <Label htmlFor="monthly_retainer_usd">Monthly Retainer (USD)</Label>
              <Input
                id="monthly_retainer_usd"
                type="number"
                step="any"
                min="0"
                value={data.monthly_retainer_usd ?? ''}
                onChange={(e) => {
                  const raw = e.target.value
                  handleInputChange(
                    'monthly_retainer_usd',
                    raw === '' ? null : Number(raw)
                  )
                }}
                placeholder="e.g. 5000"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Normalised USD amount. Feeds the dashboard ROI hero card.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="retainer_currency">Retainer Currency (raw)</Label>
              <Select
                value={data.retainer_currency || undefined}
                onValueChange={(value) =>
                  handleInputChange('retainer_currency', value || null)
                }
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {RETAINER_CURRENCY_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Raw currency (before USD normalisation).
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onboarding_date">Contract Start Date</Label>
            <Input
              id="onboarding_date"
              type="date"
              value={data.onboarding_date ?? ''}
              onChange={(e) => handleInputChange('onboarding_date', e.target.value || null)}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Contract start anchor. Feeds dashboard ROI hero + upcoming
              analytics. In Attio this lives on the current Deal as{' '}
              <code>Start Date</code>.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attio_record_id">Attio Record ID</Label>
            <Input
              id="attio_record_id"
              value={data.attio_record_id ?? ''}
              onChange={(e) =>
                handleInputChange('attio_record_id', e.target.value || null)
              }
              placeholder="e.g. abc12345-6789-..."
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Foreign key into Attio CRM. Populated automatically once the
              Attio MCP sync is wired; editable here as a manual override.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ClickUp Integration */}
      <Card>
        <CardHeader>
          <CardTitle>ClickUp Integration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="clickup_backlog_url">ClickUp Backlog URL</Label>
            <Input
              id="clickup_backlog_url"
              value={data.clickup_backlog_url || ''}
              onChange={(e) => handleInputChange('clickup_backlog_url', e.target.value)}
              placeholder="https://app.clickup.com/123456/v/li/123456789"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              This should be a direct link to the client's backlog or main project in ClickUp
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Notes */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle>Onboarding Notes</CardTitle>
          </div>
          <CardDescription>
            Add any important notes about the client onboarding process
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="onboarding_notes">Notes</Label>
            <Textarea
              id="onboarding_notes"
              value={data.onboarding_notes || ''}
              onChange={(e) => handleInputChange('onboarding_notes', e.target.value)}
              placeholder="Enter any important onboarding notes, special requirements, or context about this client..."
              rows={4}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Validation Summary */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-blue-800">
            <Target className="h-4 w-4" />
            <span className="text-sm font-medium">
              Required fields: {data.name ? '✅ Client Name' : '❌ Client Name'} | {data.website ? '✅ Website' : '❌ Website'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
